import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import {
  CartLink,
  CurrencySwitcher,
  NamedLink,
} from '../../../../components';

import logoWhite from '../../../../assets/logo-white.png';
import logoBlack from '../../../../assets/logo-black.png';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import TopbarProfileMenu from '../TopbarProfileMenu/TopbarProfileMenu';

import css from './TopbarDesktop.module.css';
import IconCollection from '../../../../components/IconCollection/IconCollection';

const SignupLink = () => {
  return (
    <NamedLink id="signup-link" name="SignupPage" className={css.signupLink}>
      <span>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink id="login-link" name="LoginPage" className={css.loginLink}>
      <span>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

/** Login page topbar: muted prompt + link to signup */
const AuthHeaderSignupPrompt = () => (
  <div className={css.authHeaderAuthPrompt}>
    <span className={css.authHeaderPromptText}>
      <FormattedMessage id="TopbarDesktop.authLoginPagePrompt" />
    </span>{' '}
    <NamedLink id="signup-link" name="SignupPage" className={css.authHeaderCtaLink}>
      <FormattedMessage id="TopbarDesktop.authLoginPageRegisterLink" />
    </NamedLink>
  </div>
);

/** Signup page topbar: muted prompt + link to login */
const AuthHeaderLoginPrompt = () => (
  <div className={css.authHeaderAuthPrompt}>
    <span className={css.authHeaderPromptText}>
      <FormattedMessage id="TopbarDesktop.authSignupPagePrompt" />
    </span>{' '}
    <NamedLink id="login-link" name="LoginPage" className={css.authHeaderCtaLink}>
      <FormattedMessage id="TopbarDesktop.authSignupPageSignInLink" />
    </NamedLink>
  </div>
);

const InboxLink = ({ notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      id="inbox-link"
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: inboxTab }}
    >
      <span className={css.topbarLinkLabel}>
        {/* <FormattedMessage id="TopbarDesktop.inbox" /> */}
        <svg style={{ fill: "transparent" }} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10.2673 20.9998C10.4428 21.3038 10.6953 21.5563 10.9993 21.7318C11.3033 21.9073 11.6482 21.9997 11.9993 21.9997C12.3503 21.9997 12.6952 21.9073 12.9992 21.7318C13.3033 21.5563 13.5557 21.3038 13.7313 20.9998M11.6793 2.00879C10.1456 2.0907 8.7018 2.7576 7.64512 3.87213C6.58844 4.98666 5.99939 6.46397 5.99927 7.99979C5.99927 12.4988 4.58827 13.9558 3.26127 15.3258C3.13063 15.469 3.04442 15.647 3.01312 15.8383C2.98183 16.0296 3.00679 16.2258 3.08498 16.4032C3.16316 16.5805 3.2912 16.7313 3.45352 16.8373C3.61585 16.9432 3.80545 16.9996 3.99927 16.9998H19.9993C20.1931 16.9999 20.3827 16.9436 20.5451 16.8379C20.7076 16.7322 20.8358 16.5815 20.9142 16.4043C20.9926 16.2271 21.0178 16.0309 20.9867 15.8396C20.9557 15.6483 20.8697 15.4701 20.7393 15.3268C19.9153 14.4768 19.0613 13.5958 18.5293 11.9788" stroke="#64748B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 6.65685 16.3431 8 18 8Z" stroke="#64748B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>

        {notificationDot}
      </span>
    </NamedLink>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {boolean} props.showBecomeSellerLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    showBecomeSellerLink = true,
    inboxTab,
    isScrolled = false,
    loginHeader,
    signupHeader,
    cartCount = 0,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const showCartLink = mounted;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} />
  ) : null;
  const cartLinkMaybe = showCartLink ? (
    <CartLink className={css.topbarLink} count={cartCount} showLabel={false} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <TopbarProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      intl={intl}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );
  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      <div
        className={classNames(
          css.topbarDesktopLeft,
          (loginHeader || signupHeader) && css.authHeaderLeft
        )}
      >
        <NamedLink name="LandingPage">
          {/* <IconCollection name="logo_black" /> */}
          {currentPage !== 'LandingPage' ? (
            <img src={logoBlack} className={css.logo} alt="logo" />
          ) : isScrolled ? (
            <img src={logoBlack} className={css.logo} alt="logo" />
          ) : (
            <img src={logoWhite} className={css.logo} alt="logo" />
          )}
        </NamedLink>
        {showBecomeSellerLink ? (
          <NamedLink
            name="CMSPage"
            params={{ pageId: 'become-seller' }}
            className={classNames(
              css.becomeASellerLink,
              (currentPage !== 'LandingPage' || isScrolled) && css.scrolledBecomeASellerLink
            )}
          >
            Become a Seller
          </NamedLink>
        ) : null}
      </div>
      {/* {searchFormMaybe} */}

      {loginHeader ? (
        <div className={css.topbarDesktopRight}>
          <AuthHeaderSignupPrompt />
        </div>
      ) : signupHeader ? (
        <div className={css.topbarDesktopRight}>
          <AuthHeaderLoginPrompt />
        </div>
      ) : (
        <div className={css.topbarDesktopRight}>
          <CustomLinksMenu
            key={`${isAuthenticated}-${showCreateListingsLink}`}
            currentPage={currentPage}
            customLinks={customLinks}
            intl={intl}
            hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
            showCreateListingsLink={showCreateListingsLink}
          />

          {mounted ? <CurrencySwitcher className={css.currencySwitcher} /> : null}
          {cartLinkMaybe}
          {inboxLinkMaybe}
          {profileMenuMaybe}

          {loginLinkMaybe}
          {signupLinkMaybe}
        </div>
      )}
    </nav>
  );
};

export default TopbarDesktop;
